// src/services/pageAnalyticsService.js
// Service for tracking page views and time on page

import { supabase } from '../config';

class PageAnalyticsService {
  constructor() {
    this.currentPage = null;
    this.pageStartTime = null;
    this.pageViewTimer = null;
    this.isEnabled = true;
  }

  /**
   * Track a page view
   * @param {string} pagePath - The route path (e.g., '/equity/companies')
   * @param {string} pageName - Human-readable page name (e.g., 'Companies')
   * @param {string} module - Module name (e.g., 'equity', 'dataroom', 'admin')
   */
  async trackPageView(pagePath, pageName = null, module = null) {
    if (!this.isEnabled) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // End previous page tracking if exists
      if (this.currentPage && this.pageStartTime) {
        const timeOnPage = Date.now() - this.pageStartTime;
        await this.logTimeOnPage(this.currentPage, timeOnPage);
      }

      // Start tracking new page
      this.currentPage = {
        path: pagePath,
        name: pageName || this.extractPageName(pagePath),
        module: module || this.extractModule(pagePath)
      };
      this.pageStartTime = Date.now();

      // Log page view to database
      await this.logPageView(pagePath, pageName, module);

      // Set timer to log time on page when user leaves (after 30 seconds minimum)
      if (this.pageViewTimer) {
        clearTimeout(this.pageViewTimer);
      }
      this.pageViewTimer = setTimeout(async () => {
        if (this.currentPage && this.pageStartTime) {
          const timeOnPage = Date.now() - this.pageStartTime;
          await this.logTimeOnPage(this.currentPage, timeOnPage);
        }
      }, 30000); // Log after 30 seconds

    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  /**
   * Log page view to database
   */
  async logPageView(pagePath, pageName, module) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Try to insert into page_views table (create if doesn't exist)
      const { error } = await supabase
        .from('page_views')
        .insert({
          user_id: user.id,
          page_path: pagePath,
          page_name: pageName || this.extractPageName(pagePath),
          module: module || this.extractModule(pagePath),
          viewed_at: new Date().toISOString(),
          session_id: this.getSessionId()
        });

      if (error) {
        // Table might not exist, log to console in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Page view tracked (page_views table may not exist):', {
            pagePath,
            pageName,
            module
          });
        }
      }
    } catch (error) {
      // Silently fail - don't break the app if analytics fails
      console.error('Error logging page view:', error);
    }
  }

  /**
   * Log time spent on page
   */
  async logTimeOnPage(pageInfo, timeMs) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Only log if user spent meaningful time (at least 5 seconds)
      if (timeMs < 5000) return;

      const { error } = await supabase
        .from('page_views')
        .update({
          time_on_page_ms: timeMs,
          time_on_page_seconds: Math.round(timeMs / 1000)
        })
        .eq('user_id', user.id)
        .eq('page_path', pageInfo.path)
        .order('viewed_at', { ascending: false })
        .limit(1);

      if (error && process.env.NODE_ENV === 'development') {
        console.log('Time on page tracked:', {
          page: pageInfo.path,
          time: Math.round(timeMs / 1000) + 's'
        });
      }
    } catch (error) {
      console.error('Error logging time on page:', error);
    }
  }

  /**
   * Extract page name from path
   */
  extractPageName(path) {
    if (!path) return 'Unknown';
    const parts = path.split('/').filter(p => p);
    if (parts.length === 0) return 'Home';
    return parts[parts.length - 1].charAt(0).toUpperCase() + 
           parts[parts.length - 1].slice(1).replace(/-/g, ' ');
  }

  /**
   * Extract module from path
   */
  extractModule(path) {
    if (!path) return 'unknown';
    if (path.startsWith('/equity')) return 'equity';
    if (path.startsWith('/dataroom')) return 'dataroom';
    if (path.startsWith('/capraise')) return 'capraise';
    if (path.startsWith('/questions')) return 'questions';
    if (path.startsWith('/adminhq') || path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/my-account')) return 'my-account';
    return 'other';
  }

  /**
   * Get session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('page_analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('page_analytics_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Clean up when user navigates away
   */
  cleanup() {
    if (this.pageViewTimer) {
      clearTimeout(this.pageViewTimer);
      this.pageViewTimer = null;
    }
    if (this.currentPage && this.pageStartTime) {
      const timeOnPage = Date.now() - this.pageStartTime;
      this.logTimeOnPage(this.currentPage, timeOnPage);
    }
    this.currentPage = null;
    this.pageStartTime = null;
  }
}

// Export singleton instance
const pageAnalyticsService = new PageAnalyticsService();
export default pageAnalyticsService;

